import { type FlowGraph, defaultLaunchCfg, type GraphWallet, type GraphEdge, type AirdropList } from "./graph";
import { uid } from "./choreography";
import { getStoredHeliosKey, getSolanaRpcUrl } from "@/lib/solanaRpc";


export async function reconstructFlowGraph(mint: string): Promise<FlowGraph> {
  // 1. Fetch recent transactions for the mint via backend proxy
  const url = `/api/helius/transactions/${mint}`;
  const response = await fetch(url);
  
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(`Helius API error: ${err.error || response.statusText}`);
  }

  const txs = await response.json();
  
  const walletsMap = new Map<string, GraphWallet>();
  const edges: GraphEdge[] = [];
  
  // Track known wallets (we'll place them in a spiral or grid)
  let walletCount = 0;
  function getOrCreateWallet(pubkey: string): string {
    if (!walletsMap.has(pubkey)) {
      walletCount++;
      const id = uid("w");
      // Simple grid layout
      const perRow = 5;
      const x = 60 + ((walletCount - 1) % perRow) * 220;
      const y = 60 + Math.floor((walletCount - 1) / perRow) * 200;
      
      walletsMap.set(pubkey, {
        id,
        label: `Wallet ${walletCount}`,
        group: "core",
        fundingSol: 0,
        enabled: true,
        x,
        y,
        publicKey: pubkey,
      });
    }
    return walletsMap.get(pubkey)!.id;
  }

  // Very basic extraction of token transfers
  // We look for token transfers matching the mint
  let orderCounter = 1;
  const launchCreatorWallets = new Set<string>();

  for (const tx of txs) {
    if (!tx.tokenTransfers) continue;

    for (const transfer of tx.tokenTransfers) {
      if (transfer.mint === mint) {
        // If fromUserAccount is empty, it's a mint event
        if (!transfer.fromUserAccount) {
          launchCreatorWallets.add(transfer.toUserAccount);
        } else {
          const fromId = launchCreatorWallets.has(transfer.fromUserAccount) ? "launch" : getOrCreateWallet(transfer.fromUserAccount);
          const toId = getOrCreateWallet(transfer.toUserAccount);
          
          edges.push({
            id: uid("edge"),
            source: fromId,
            target: toId,
            kind: "rotate", // Using rotate to signify token movement, or 'buy' if it was SOL
            order: orderCounter++,
            sol: transfer.tokenAmount || 0,
            sellPct: 100, // Simplification
            waitSec: 0,
          });
        }
      }
    }
  }

  // Ensure at least one wallet exists so the graph is valid
  if (walletsMap.size === 0) {
    getOrCreateWallet("DummyAddress111111111111111111111111111111111");
  }

  return {
    name: `Reconstructed ${mint.slice(0, 4)}`,
    devBuySol: 0,
    solUsd: 150,
    bucketMs: 5000,
    launch: defaultLaunchCfg(),
    wallets: Array.from(walletsMap.values()),
    edges,
    sells: [],
  };
}

export async function extractHolders(mint: string): Promise<AirdropList> {
  const headers: Record<string, string> = {};
  const heliosKey = getStoredHeliosKey();
  if (heliosKey && heliosKey !== "backend-configured") {
    headers["x-helius-api-key"] = heliosKey;
  }
  const rpcUrl = getSolanaRpcUrl();
  if (rpcUrl && !rpcUrl.startsWith("/sol-rpc")) {
    headers["x-solana-rpc-url"] = rpcUrl;
  }
  const res = await fetch(`/api/extract-holders/${mint}`, { headers });
  if (!res.body) throw new Error("No readable stream");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const recipients = [];
  let totalAmount = 0;
  
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";
    
    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        const data = JSON.parse(line);
        if (data.amount > 0) {
          recipients.push({ address: data.address, amount: data.amount, pct: 0 });
          totalAmount += data.amount;
        }
      } catch(e) {}
    }
  }

  // Calculate percentages based on the total extracted amount
  if (totalAmount > 0) {
    for (const r of recipients) {
      r.pct = (r.amount / totalAmount) * 100;
    }
  }

  return {
    id: uid("al"),
    name: `Extracted ${mint.slice(0, 4)}`,
    sourceMint: mint,
    recipients
  };
}
